import React, { useState, useEffect } from 'react';
import api from '../api';
import { GROUPS, R32_MATCHES, R16_MATCHES, QF_MATCHES, SF_MATCHES } from '../data/tournament';

const ROUND_MATCHES = [
  ...GROUPS.flatMap(g => [
    { id: `group_${g}_pos1`, label: `Group ${g} — 1st Place`, round: 'groups' },
    { id: `group_${g}_pos2`, label: `Group ${g} — 2nd Place`, round: 'groups' },
    { id: `group_${g}_pos3`, label: `Group ${g} — 3rd Place`, round: 'groups' },
    { id: `third_advance_${g}`, label: `Group ${g} 3rd advances`, round: 'groups' },
  ]),
  ...R32_MATCHES.map(m => ({ id: m.id, label: `R32 ${m.label} winner`, round: 'r32' })),
  ...R16_MATCHES.map(m => ({ id: m.id, label: `R16 ${m.label} winner`, round: 'r16' })),
  ...QF_MATCHES.map(m => ({ id: m.id, label: `QF ${m.label} winner`, round: 'qf' })),
  ...SF_MATCHES.map(m => ({ id: m.id, label: `SF ${m.label} winner`, round: 'sf' })),
  { id: '3rd_place', label: '3rd Place Match winner', round: 'sf' },
  { id: 'final', label: 'Final winner (Champion)', round: 'final' },
];

export default function Admin() {
  const [leagues, setLeagues] = useState([]);
  const [teams, setTeams] = useState([]);
  const [results, setResults] = useState({});
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('results');
  const [simLeagueId, setSimLeagueId] = useState('');
  const [simCount, setSimCount] = useState(10);
  const [simWorking, setSimWorking] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/admin/leagues'),
      api.get('/teams'),
      api.get('/admin/results'),
    ]).then(([lRes, tRes, rRes]) => {
      setLeagues(lRes.data);
      setTeams(tRes.data);
      const resMap = {};
      for (const r of rRes.data) resMap[r.match_id] = r.winner_team_id;
      setResults(resMap);
    }).finally(() => setLoading(false));
  }, []);

  const showMsg = (text) => { setMsg(text); setTimeout(() => setMsg(''), 4000); };

  const handleResultChange = async (matchId, teamId, round) => {
    try {
      await api.post('/admin/results', { match_id: matchId, winner_team_id: teamId || null, round });
      setResults(prev => ({ ...prev, [matchId]: teamId || null }));
    } catch (err) {
      showMsg('Error saving result: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleLock = async (leagueId, lock) => {
    const message = lock ? prompt('Optional lock message to show players:') : null;
    try {
      await api.post(`/admin/league/${leagueId}/lock`, { locked: lock, message });
      setLeagues(prev => prev.map(l => l.id === leagueId ? { ...l, is_locked: lock } : l));
      showMsg(lock ? 'League locked.' : 'League unlocked.');
    } catch (err) {
      showMsg('Error: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleRecalculate = async (leagueId) => {
    try {
      const res = await api.post(`/admin/recalculate/${leagueId}`);
      showMsg(res.data.message);
    } catch (err) {
      showMsg('Error: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSimulate = async () => {
    if (!window.confirm('Run a full random simulation? This replaces all current tournament results and recalculates every bracket score.')) return;
    setSimWorking(true);
    try {
      const res = await api.post('/admin/simulate');
      showMsg('🎲 ' + res.data.message);
      const rRes = await api.get('/admin/results');
      const resMap = {};
      for (const r of rRes.data) resMap[r.match_id] = r.winner_team_id;
      setResults(resMap);
    } catch (err) {
      showMsg('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setSimWorking(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset simulation? This clears ALL tournament results and zeros all scores.')) return;
    setSimWorking(true);
    try {
      const res = await api.post('/admin/reset-simulation');
      setResults({});
      showMsg('🔄 ' + res.data.message);
    } catch (err) {
      showMsg('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setSimWorking(false);
    }
  };

  const handleGeneratePlayers = async (e) => {
    e.preventDefault();
    if (!simLeagueId) return showMsg('Select a league first.');
    setSimWorking(true);
    try {
      const res = await api.post('/admin/generate-test-players', { league_id: parseInt(simLeagueId), count: parseInt(simCount) });
      showMsg('👥 ' + res.data.message);
      const lRes = await api.get('/admin/leagues');
      setLeagues(lRes.data);
    } catch (err) {
      showMsg('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setSimWorking(false);
    }
  };

  const handleClearTestPlayers = async () => {
    if (!window.confirm('Remove ALL test players and their brackets?')) return;
    setSimWorking(true);
    try {
      const res = await api.post('/admin/clear-test-players');
      showMsg('🗑 ' + res.data.message);
      const lRes = await api.get('/admin/leagues');
      setLeagues(lRes.data);
    } catch (err) {
      showMsg('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setSimWorking(false);
    }
  };

  if (loading) return <div className="loading">Loading admin panel…</div>;

  const sections = [
    { id: 'results', label: 'Enter Results' },
    { id: 'leagues', label: 'Manage Leagues' },
    { id: 'simulation', label: '🎲 Simulation' },
  ];

  const renderRoundResults = (roundLabel, matchDefs, roundKey) => (
    <div className="admin-section" key={roundKey}>
      <h2>{roundLabel}</h2>
      <div className="result-grid">
        {matchDefs.filter(m => m.round === roundKey).map(m => {
          const currentVal = results[m.id] || '';
          return (
            <div key={m.id} className="result-item">
              <label>{m.label}</label>
              <select
                value={currentVal}
                onChange={e => handleResultChange(m.id, parseInt(e.target.value) || null, roundKey)}
              >
                <option value="">— Not yet played —</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.name} ({t.group_name})</option>)}
              </select>
              {currentVal && <div className="result-set">✓ Set: {teams.find(t => t.id === parseInt(currentVal))?.name}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );

  // Advancing 3rd-place teams (for 3rd-slot R32 matches)
  const advancingThirds = GROUPS
    .filter(g => results[`third_advance_${g}`])
    .map(g => teams.find(t => t.id === results[`third_advance_${g}`]))
    .filter(Boolean);

  const renderKnockoutRound = (roundLabel, matches, prevMatches, roundKey) => {
    return (
      <div className="admin-section">
        <h2>{roundLabel}</h2>
        <div className="result-grid">
          {matches.map(m => {
            const currentWinner = results[m.id] || null;
            let team1 = null, team2 = null;

            if (m.slot1 !== undefined) {
              // R32: resolve from group results or 3rd-place advance
              const resolveR32Slot = (slot) => {
                const gm = slot.match(/^([12])([A-L])$/);
                if (gm) { const tid = results[`group_${gm[2]}_pos${gm[1]}`]; return tid ? teams.find(t => t.id === tid) : null; }
                const tm = slot.match(/^3([A-L])$/);
                if (tm) { const tid = results[`third_advance_${tm[1]}`]; return tid ? teams.find(t => t.id === tid) : null; }
                const rm = slot.match(/^3rd(\d+)$/);
                if (rm) { const tid = results[`third_rank_${rm[1]}`]; return tid ? teams.find(t => t.id === tid) : null; }
                return null;
              };
              team1 = resolveR32Slot(m.slot1);
              team2 = resolveR32Slot(m.slot2);
            } else if (prevMatches) {
              // R16/QF/SF: resolve from previous round winners
              const feeders = prevMatches.filter(pm => pm.nextMatch === m.id);
              const top = feeders.find(f => f.nextSlot === 'top');
              const bot = feeders.find(f => f.nextSlot === 'bot');
              team1 = top && results[top.id] ? teams.find(t => t.id === results[top.id]) : null;
              team2 = bot && results[bot.id] ? teams.find(t => t.id === results[bot.id]) : null;
            }

            const vsLabel = m.slot1Label && m.slot2Label ? ` — ${m.slot1Label} vs ${m.slot2Label}` : '';

            return (
              <div key={m.id} className="result-item">
                <label>{m.label}{vsLabel}</label>
                {team1 && team2 ? (
                  <div style={{ display:'flex', gap:'0.4rem', alignItems:'center', marginTop:'0.3rem', flexWrap:'wrap' }}>
                    <button
                      className={`btn btn-sm ${currentWinner === team1.id ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => handleResultChange(m.id, currentWinner === team1.id ? null : team1.id, roundKey)}
                      style={{ fontSize:'0.82rem' }}
                    >
                      {team1.name}
                    </button>
                    <span style={{ color:'var(--text-muted)', fontSize:'0.78rem' }}>vs</span>
                    <button
                      className={`btn btn-sm ${currentWinner === team2.id ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => handleResultChange(m.id, currentWinner === team2.id ? null : team2.id, roundKey)}
                      style={{ fontSize:'0.82rem' }}
                    >
                      {team2.name}
                    </button>
                    {currentWinner && (
                      <span style={{ fontSize:'0.78rem', color:'var(--gold)', marginLeft:'0.25rem' }}>
                        ✓ {teams.find(t => t.id === currentWinner)?.name} advances
                      </span>
                    )}
                  </div>
                ) : (
                  <>
                    <select
                      value={currentWinner || ''}
                      onChange={e => handleResultChange(m.id, parseInt(e.target.value) || null, roundKey)}
                    >
                      <option value="">— Not yet played —</option>
                      {teams.map(t =>
                        <option key={t.id} value={t.id}>{t.name} ({t.group_name})</option>
                      )}
                    </select>
                    {currentWinner && <div className="result-set">✓ {teams.find(t => t.id === currentWinner)?.name}</div>}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="admin-page">
      <h1>🛠 Admin Panel</h1>
      {msg && <div className="success-msg mb-2">{msg}</div>}

      <div className="bracket-tabs mb-2">
        {sections.map(s => (
          <button key={s.id} className={`bracket-tab ${activeSection === s.id ? 'active' : ''}`} onClick={() => setActiveSection(s.id)}>{s.label}</button>
        ))}
      </div>

      {activeSection === 'results' && (
        <>
          <p className="text-muted mb-2">
            Set official tournament results here. Scores are recalculated automatically when you save a result.
            After setting results, use "Manage Leagues" to recalculate all bracket scores.
          </p>
          {renderRoundResults('Group Stage Results', ROUND_MATCHES, 'groups')}
          {renderKnockoutRound('Round of 32', R32_MATCHES, null, 'r32')}
          {renderKnockoutRound('Round of 16', R16_MATCHES, R32_MATCHES, 'r16')}
          {renderKnockoutRound('Quarter-Finals', QF_MATCHES, R16_MATCHES, 'qf')}
          {renderKnockoutRound('Semi-Finals', SF_MATCHES, QF_MATCHES, 'sf')}
          {(() => {
            const finalTeam1 = results['sf_m01'] ? teams.find(t => t.id === results['sf_m01']) : null;
            const finalTeam2 = results['sf_m02'] ? teams.find(t => t.id === results['sf_m02']) : null;
            const sf1t1 = results['qf_m01'] ? teams.find(t => t.id === results['qf_m01']) : null;
            const sf1t2 = results['qf_m02'] ? teams.find(t => t.id === results['qf_m02']) : null;
            const sf2t1 = results['qf_m03'] ? teams.find(t => t.id === results['qf_m03']) : null;
            const sf2t2 = results['qf_m04'] ? teams.find(t => t.id === results['qf_m04']) : null;
            const sf1Loser = results['sf_m01'] ? (sf1t1?.id === results['sf_m01'] ? sf1t2 : sf1t1) : null;
            const sf2Loser = results['sf_m02'] ? (sf2t1?.id === results['sf_m02'] ? sf2t2 : sf2t1) : null;
            const renderFinalPicker = (matchId, t1, t2, roundKey, label) => {
              const cur = results[matchId] || null;
              return (
                <div className="result-item">
                  <label>{label}</label>
                  {t1 && t2 ? (
                    <div style={{ display:'flex', gap:'0.4rem', alignItems:'center', marginTop:'0.3rem', flexWrap:'wrap' }}>
                      <button className={`btn btn-sm ${cur === t1.id ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize:'0.82rem' }}
                        onClick={() => handleResultChange(matchId, cur === t1.id ? null : t1.id, roundKey)}>{t1.name}</button>
                      <span style={{ color:'var(--text-muted)', fontSize:'0.78rem' }}>vs</span>
                      <button className={`btn btn-sm ${cur === t2.id ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize:'0.82rem' }}
                        onClick={() => handleResultChange(matchId, cur === t2.id ? null : t2.id, roundKey)}>{t2.name}</button>
                      {cur && <span style={{ fontSize:'0.78rem', color:'var(--gold)', marginLeft:'0.25rem' }}>✓ {teams.find(t => t.id === cur)?.name}</span>}
                    </div>
                  ) : (
                    <>
                      <select value={cur || ''} onChange={e => handleResultChange(matchId, parseInt(e.target.value) || null, roundKey)}>
                        <option value="">— Not yet played —</option>
                        {teams.map(t => <option key={t.id} value={t.id}>{t.name} ({t.group_name})</option>)}
                      </select>
                      {cur && <div className="result-set">✓ {teams.find(t => t.id === cur)?.name}</div>}
                    </>
                  )}
                </div>
              );
            };
            return (
              <div className="admin-section">
                <h2>Final &amp; 3rd Place</h2>
                <div className="result-grid">
                  {renderFinalPicker('3rd_place', sf1Loser, sf2Loser, 'sf', '3rd Place Match winner')}
                  {renderFinalPicker('final', finalTeam1, finalTeam2, 'final', 'Final — World Cup Champion')}
                </div>
              </div>
            );
          })()}
        </>
      )}

      {activeSection === 'leagues' && (
        <div className="admin-section">
          <h2>All Leagues</h2>
          {leagues.length === 0 ? (
            <div className="empty-state">No leagues yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {leagues.map(l => (
                <div key={l.id} className="card" style={{ padding: '1rem 1.25rem' }}>
                  <div className="flex-between">
                    <div>
                      <div style={{ fontWeight: 700 }}>{l.name}</div>
                      <div className="text-muted" style={{ fontSize: '0.82rem' }}>
                        Creator: {l.creator} · {l.member_count} members · Code: <span className="invite-code">{l.invite_code}</span>
                        {l.is_locked && <> · <span className="tag tag-locked">🔒 Locked</span></>}
                      </div>
                    </div>
                    <div className="flex-gap">
                      <button
                        className={`btn btn-sm ${l.is_locked ? 'btn-secondary' : 'btn-danger'}`}
                        onClick={() => handleLock(l.id, !l.is_locked)}
                      >
                        {l.is_locked ? '🔓 Unlock' : '🔒 Lock'}
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => handleRecalculate(l.id)}>
                        ↻ Recalculate Scores
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeSection === 'simulation' && (
        <div>
          <p className="text-muted mb-2">
            Use these tools to test the app before the tournament starts. Generate fake players, run a random simulation, then check the leaderboard to see how scoring looks.
          </p>

          {/* Step 1 */}
          <div className="admin-section">
            <h2>Step 1 — Add Test Players to a League</h2>
            <form onSubmit={handleGeneratePlayers} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ margin: 0, minWidth: 220 }}>
                <label>League</label>
                <select value={simLeagueId} onChange={e => setSimLeagueId(e.target.value)}>
                  <option value="">— Select a league —</option>
                  {leagues.map(l => <option key={l.id} value={l.id}>{l.name} ({l.member_count} members)</option>)}
                </select>
              </div>
              <div className="form-group" style={{ margin: 0, minWidth: 100 }}>
                <label>How many</label>
                <input type="number" min={1} max={49} value={simCount} onChange={e => setSimCount(e.target.value)} />
              </div>
              <button type="submit" className="btn btn-primary" disabled={simWorking || !simLeagueId}>
                {simWorking ? 'Working…' : '👥 Generate Players'}
              </button>
            </form>
            <p className="text-muted mt-1" style={{ fontSize: '0.8rem' }}>
              Creates fake accounts (testplayer_…) with random brackets. Password for all test accounts: <code>test1234</code>
            </p>
          </div>

          {/* Step 2 */}
          <div className="admin-section">
            <h2>Step 2 — Simulate Tournament Results</h2>
            <p className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
              Randomly picks winners for every match (group stage through final) and recalculates all bracket scores across all leagues.
            </p>
            <div className="flex-gap">
              <button className="btn btn-primary" onClick={handleSimulate} disabled={simWorking}>
                {simWorking ? 'Running…' : '🎲 Run Simulation'}
              </button>
              <button className="btn btn-secondary" onClick={handleReset} disabled={simWorking}>
                🔄 Reset (Clear All Results)
              </button>
            </div>
          </div>

          {/* Step 3 */}
          <div className="admin-section">
            <h2>Step 3 — Check the Leaderboard</h2>
            <p className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
              Go to any league's leaderboard to see scores. Run the simulation multiple times to see different outcomes. Reset when done testing.
            </p>
            {leagues.map(l => (
              <div key={l.id} style={{ marginBottom: '0.5rem' }}>
                <a href={`/league/${l.id}/leaderboard`} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">
                  View "{l.name}" leaderboard →
                </a>
              </div>
            ))}
          </div>

          {/* Cleanup */}
          <div className="admin-section">
            <h2>Cleanup</h2>
            <p className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
              Remove all test players and their brackets when you're done testing.
            </p>
            <button className="btn btn-danger btn-sm" onClick={handleClearTestPlayers} disabled={simWorking}>
              🗑 Remove All Test Players
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
